/**
 * Thin send/receive helper around LiveKit data-channels using the envelope
 * format from datachannel.ts. Kept dependency-free: it only relies on a
 * caller-supplied `publishData` callback (so it's trivially mockable in tests
 * and works with both browser LiveKit `Room` and the localhost WS bridge).
 */

import {
  envelope,
  parseEnvelope,
  type DcEnvelope,
  type DcType,
} from "./datachannel.js";

export type PublishFn = (data: Uint8Array, reliable: boolean) => unknown;

/** Encode + publish a single envelope. */
export function publishEnvelope<T extends DcType, P>(
  publish: PublishFn,
  type: T,
  payload: P,
  reliable = true,
): DcEnvelope<T, P> {
  const env = envelope(type, payload);
  const data = new TextEncoder().encode(JSON.stringify(env));
  publish(data, reliable);
  return env;
}

/**
 * Decode incoming bytes into an envelope. Returns null on bad input — caller
 * should ignore (do NOT crash on garbage from the wire).
 */
export function decodeIncoming(raw: Uint8Array | string): DcEnvelope | null {
  return parseEnvelope(raw);
}
